/* Segments — the saved-audience tab.

   mockups/crm-v3-campaigns-v1.html, section #segments (approved 2026-09-06):
   a 400px list on the left, the definition on the right. A segment is rules,
   so every number on this screen is computed the moment it is drawn — there
   are no stored counts to go stale, and editing a rule moves the counts before
   the operator's finger leaves the control.

   All the arithmetic is src/lib/segments.js, which is pure and shared with
   tools/crm/segment.mjs, so a segment resolved in the chat and the same
   segment opened here name the same people.

   The consent rule is drawn and cannot be removed. It matches everybody on its
   own: membership ignores consent by design, eligibility is applied to every
   segment afterwards, and the send-time callable applies it again. */
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { C, font, mono, inp, sel, btnS, btnP, chip } from "../../lib/styles.js";
import { fmt, BLANK_SEGMENT } from "../../lib/utils.js";
import { CONTACT_ROLES, ROLE_COLORS, CONSENT_STATES } from "../../lib/constants.js";
import { S, cellStyle, ROW_CLASS, consentDotStyle, consentColor } from "./listStyles.js";
import { ListRowStyle } from "./ListParts.jsx";
import { buildContactRows, parseTags } from "./listModel.js";
import {
  SEGMENT_FIELDS, VALUE_BUCKETS, fieldDef, isConsentRule, normalizeRules, consentValue,
  buildSegmentIndex, resolveSegment, describeSegment, validateSegment, memberReason,
} from "../../lib/segments.js";
import { BULK_ELIGIBLE } from "../../lib/consent.js";

const SAMPLE_SIZE = 5;
/* The editor's identity for an unsaved record. It carries the record's OWN id,
   because a constant here meant "New" pressed while an unsaved draft was open
   did not change the open id — so the reset below never ran and the new
   segment opened carrying the old one's rules and name. */
const newKey = (record) => `new:${record?.id || "?"}`;

const arr = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const sameShape = (a, b) => JSON.stringify([a?.name, a?.rules, a?.note]) === JSON.stringify([b?.name, b?.rules, b?.note]);

/* The four list chips from the mockup. "Used recently" is a date on the
   segment, not a campaign join — a segment a campaign froze last week is a
   segment somebody used last week. */
const LIST_CHIPS = [
  { key: "all", label: "All" },
  { key: "mine", label: "Mine" },
  { key: "chat", label: "From chat" },
  { key: "recent", label: "Used recently" },
];
const RECENT_DAYS = 30;

const dateOf = (v) => String(v || "").slice(0, 10);

export default function SegmentsView({
  segments = [],
  contacts = [],
  clients = [],
  jobs = [],
  savedQuotes = [],
  campaigns = [],
  user,
  saveSegment,
  removeSegment,
  activeSegmentId,
  setActiveSegmentId,
  newSegment,
  setNewSegment,
  campaignsTab = null,
  onOpenCampaign,
  isMobile,
}) {
  const [listFilter, setListFilter] = useState("all");
  const [draft, setDraft] = useState(null);
  /* A new segment opens with the name field waiting. The initializer matters
     as much as the reset below: arriving from "Save as segment" MOUNTS this
     view with the new record already in hand, so the id never changes and the
     reset block never runs. */
  const [renaming, setRenaming] = useState(() => Boolean(newSegment));
  const [showAll, setShowAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState("");
  const nameRef = useRef(null);

  /* A soft-deleted segment is gone from the tab but still on the record, so a
     campaign that froze its audience keeps a name to point at. */
  const live = useMemo(() => (segments || []).filter((s) => s && !s.deletedAt), [segments]);

  /* One index over the whole book, rebuilt only when a collection changes.
     Editing a rule then costs a filter pass, not another walk over 400 jobs
     and 600 quotes — that is what keeps the counts inside a frame. */
  const index = useMemo(
    () => buildSegmentIndex({ contacts, clients, jobs, quotes: savedQuotes }),
    [contacts, clients, jobs, savedQuotes],
  );
  const data = useMemo(
    () => ({ contacts, clients, jobs, quotes: savedQuotes, index }),
    [contacts, clients, jobs, savedQuotes, index],
  );

  const openId = newSegment ? newKey(newSegment) : activeSegmentId;
  const openRecord = newSegment || live.find((s) => s.id === activeSegmentId) || null;

  /* Every edit state belongs to ONE segment, reset from the id rather than
     from the click handler — the same shape the Contacts drawer uses, and for
     the same reason: a segment becomes the open one several ways. */
  const [lastOpenId, setLastOpenId] = useState(openId);
  if (openId !== lastOpenId) {
    setLastOpenId(openId);
    setDraft(null);
    setRenaming(Boolean(newSegment));
    setShowAll(false);
    setProblem("");
  }

  const shown = draft || openRecord;
  const dirty = Boolean(shown && (newSegment || !sameShape(shown, openRecord)));

  /* What "still the same edit" means when a write lands.

     A save is a network round trip and the operator can carry on typing or
     open another segment while it is in flight. Two things have to match for
     the completion to touch the screen: the record it started on must still be
     the open one, and the draft must not have moved on since. `rev` bumps on
     every edit; `token` makes a superseded write a no-op even if the first two
     agree. Same shape as commitIfStillActive in the Contacts drawer, with the
     revision added because a segment is edited in place rather than in a form
     that closes. */
  const openIdRef = useRef(openId);
  useEffect(() => { openIdRef.current = openId; }, [openId]);
  const revRef = useRef(0);
  const tokenRef = useRef(0);

  const edit = useCallback((patch) => {
    revRef.current += 1;
    setDraft((prev) => ({ ...(prev || openRecord), ...patch }));
    setProblem("");
  }, [openRecord]);

  useEffect(() => {
    if (renaming) nameRef.current?.focus?.();
  }, [renaming]);

  /* ── the list, with its live counts ── */
  const rows = useMemo(() => live.map((s) => {
    const r = resolveSegment(s, data);
    return {
      record: s,
      id: s.id,
      name: s.name || "Untitled segment",
      definition: describeSegment(s, { clients, contacts }),
      total: r.counts.total,
      eligible: r.eligible.length,
      usedBy: (campaigns || []).filter((c) => c && c.segmentId === s.id && !c.deletedAt),
    };
  }), [live, data, clients, contacts, campaigns]);

  const me = String(user?.email || "").toLowerCase();
  const visibleRows = useMemo(() => rows.filter((r) => {
    const s = r.record;
    if (listFilter === "chat") return s.source === "chat";
    if (listFilter === "mine") return !me || String(s.createdBy || "").toLowerCase() === me;
    if (listFilter === "recent") {
      const at = dateOf(s.usedAt || s.updatedAt || s.createdAt);
      if (!at) return false;
      return (Date.now() - Date.parse(`${at}T00:00:00Z`)) / 86_400_000 <= RECENT_DAYS;
    }
    return true;
  }), [rows, listFilter, me]);

  /* ── the open segment, resolved ── */
  const resolved = useMemo(
    () => (shown ? resolveSegment(shown, data) : null),
    [shown, data],
  );

  const newSegmentFrom = useCallback((seed = {}) => {
    if (saving) return;
    const record = { ...BLANK_SEGMENT(), createdBy: user?.email || "", ...seed };
    record.rules = normalizeRules(seed.rules || record.rules);
    setActiveSegmentId(null);
    setNewSegment(record);
  }, [saving, setActiveSegmentId, setNewSegment, user]);

  /* Moving while a write is in flight is refused rather than guarded after the
     fact: the completion below would then have to decide what the operator
     meant, and there is no answer to that which is not a guess. */
  const openSegment = (id) => {
    if (saving) return;
    setNewSegment(null);
    setActiveSegmentId(id);
  };

  /**
   * Write one record and, only if the screen is still where it was, land on it.
   *
   * `select` decides what the editor shows afterwards — the saved record for a
   * Save, the copy for a Duplicate. The id comes back from `saveSegment`, not
   * from the record we sent: the provider owns `createdAt` / `updatedAt` and,
   * for a record it had to mint, the id itself.
   */
  const write = async (record, { label, keepEditing = false }) => {
    const problems = validateSegment(record);
    if (problems.length) { setProblem(problems[0]); return; }
    const fromId = openIdRef.current;
    const fromRev = revRef.current;
    const token = ++tokenRef.current;
    setSaving(true);
    try {
      const saved = (await saveSegment(record)) || record;
      // A later write has started, so this one no longer speaks for the screen.
      if (token !== tokenRef.current) return;
      /* The write landed either way — it is in Firestore. What is guarded here
         is only whether it may move the editor: if the operator has opened
         another segment or typed on since, leaving them where they are is the
         answer, and their draft stays theirs. */
      if (openIdRef.current !== fromId || (keepEditing && revRef.current !== fromRev)) return;
      setNewSegment(null);
      setActiveSegmentId(saved.id);
      setDraft(null);
      setRenaming(false);
    } catch (err) {
      if (token === tokenRef.current) setProblem(`${label} failed: ${err.message}`);
    } finally {
      if (token === tokenRef.current) setSaving(false);
    }
  };

  const commit = () => write(
    { ...shown, rules: normalizeRules(shown.rules) },
    { label: "Save", keepEditing: true },
  );

  /* Duplicate validates exactly as Save does: a copy of a segment whose rules
     do not validate is a second broken record, and it would resolve to nobody
     the moment it was opened. */
  const duplicate = () => write({
    ...BLANK_SEGMENT(),
    name: `${shown.name || "Untitled segment"} (copy)`,
    rules: normalizeRules(shown.rules),
    note: shown.note || "",
    source: "manual",
    createdBy: user?.email || "",
  }, { label: "Duplicate" });

  const closeDetail = () => {
    if (saving) return;
    setNewSegment(null);
    setActiveSegmentId(null);
  };

  const destroy = async () => {
    if (!openRecord || newSegment || saving) return;
    const used = rows.find((r) => r.id === openRecord.id)?.usedBy || [];
    const warning = used.length
      ? `\n\n${used.length} campaign${used.length === 1 ? "" : "s"} still name${used.length === 1 ? "s" : ""} it. Their frozen audiences are unaffected.`
      : "";
    if (!window.confirm(`Delete "${openRecord.name || "this segment"}"?${warning}`)) return;
    await removeSegment(openRecord.id);
    setActiveSegmentId(null);
  };

  const listPane = (
    <div style={SS.list} data-testid="segments-list">
      <div style={SS.listHead}>
        {LIST_CHIPS.map((c) => (
          <span
            key={c.key}
            role="button"
            tabIndex={0}
            aria-pressed={listFilter === c.key}
            data-testid={`segments-chip-${c.key}`}
            style={{ ...chip(listFilter === c.key), ...SS.listChip }}
            onClick={() => setListFilter(c.key)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setListFilter(c.key); } }}
          >
            {c.label}
          </span>
        ))}
        <span style={{ ...S.count, fontSize: 10.5 }} data-testid="segments-count">
          {visibleRows.length} segment{visibleRows.length === 1 ? "" : "s"}
        </span>
        <button
          type="button"
          style={{ ...btnP, padding: "4px 9px", fontSize: 11, marginLeft: 6 }}
          data-testid="segments-new"
          onClick={() => newSegmentFrom({})}
        >
          New
        </button>
      </div>
      {visibleRows.length === 0 && (
        <div style={S.empty}>
          {live.length === 0 ? "No segments yet." : "No segments match these chips."}
        </div>
      )}
      {visibleRows.map((r) => {
        const selected = !newSegment && r.id === activeSegmentId;
        return (
          <div
            key={r.id}
            role="button"
            tabIndex={0}
            aria-pressed={selected}
            data-testid={`segment-row-${r.id}`}
            style={{ ...SS.row, ...(selected ? SS.rowSel : null) }}
            onClick={() => openSegment(r.id)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openSegment(r.id); } }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={SS.rowName}>{r.name}</div>
              <div style={SS.rowDef}>{r.definition}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={SS.rowCount} data-testid={`segment-row-total-${r.id}`}>{r.total.toLocaleString()}</div>
              <div style={SS.rowEligible}>{r.eligible.toLocaleString()} eligible</div>
            </div>
            <div style={SS.rowMeta}>
              {[
                r.record.source === "chat" ? "from chat" : (r.record.createdBy || "manual"),
                dateOf(r.record.updatedAt || r.record.createdAt),
                r.usedBy.length ? `used by ${r.usedBy.map((c) => c.name || c.title || c.id).join(", ")}` : "",
              ].filter(Boolean).join(" · ")}
            </div>
          </div>
        );
      })}
      {newSegment && (
        <div style={{ ...SS.row, ...SS.rowSel }} data-testid="segment-row-new">
          <div style={{ minWidth: 0 }}>
            <div style={SS.rowName}>{newSegment.name || "New segment"}</div>
            <div style={SS.rowDef}>not saved yet</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={SS.rowCount}>{resolved ? resolved.counts.total.toLocaleString() : "—"}</div>
            <div style={SS.rowEligible}>{resolved ? `${resolved.eligible.length.toLocaleString()} eligible` : ""}</div>
          </div>
        </div>
      )}
    </div>
  );

  const detail = shown && resolved ? (
    <SegmentDetail
      segment={shown}
      resolved={resolved}
      dirty={dirty}
      saving={saving}
      problems={resolved.problems || []}
      problem={problem}
      isNew={Boolean(newSegment)}
      renaming={renaming}
      setRenaming={setRenaming}
      nameRef={nameRef}
      edit={edit}
      onSave={commit}
      onDiscard={() => {
        if (saving) return;
        if (newSegment) setNewSegment(null); else setDraft(null);
        setProblem("");
      }}
      onClose={isMobile ? closeDetail : null}
      onDuplicate={duplicate}
      onDelete={destroy}
      clients={clients}
      contacts={contacts}
      jobs={jobs}
      savedQuotes={savedQuotes}
      usedBy={rows.find((r) => r.id === shown.id)?.usedBy || []}
      campaignsTab={campaignsTab}
      onOpenCampaign={onOpenCampaign}
      showAll={showAll}
      setShowAll={setShowAll}
      isMobile={isMobile}
    />
  ) : (
    <div style={{ ...SS.detail, padding: 40, color: C.muted, fontSize: 13 }} data-testid="segment-detail-empty">
      Pick a segment on the left, or start a new one.
    </div>
  );

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "minmax(0,1fr)" : "400px minmax(0,1fr)",
        gap: 14,
        alignItems: "start",
        fontFamily: font,
      }}
      data-testid="segments-layout"
    >
      {(!isMobile || !shown) && listPane}
      {detail}
    </div>
  );
}

/* ═══ the detail panel ═════════════════════════════════════════════════════ */

function SegmentDetail({
  segment, resolved, dirty, saving, problem, problems = [], isNew, renaming, setRenaming, nameRef,
  edit, onSave, onDiscard, onDuplicate, onDelete, onClose, clients, contacts, jobs, savedQuotes,
  usedBy, campaignsTab, onOpenCampaign, showAll, setShowAll, isMobile,
}) {
  const { counts, members, excludedBy } = resolved;
  const eligible = resolved.eligible.length;
  const rules = arr(segment.rules);
  const setRules = (next) => edit({ rules: next });

  /* A field stays in the menu while it still has an op nobody has used — so
     Tag can be added twice (is any of, is none of) and Industry cannot, which
     is also what validateSegment refuses. */
  const freeOp = (f) => f.ops.find((o) => !rules.some((r) => r.field === f.key && r.op === o.key));
  const addable = SEGMENT_FIELDS.filter((f) => !f.locked && freeOp(f));

  const addRule = (fieldKey) => {
    const def = fieldDef(fieldKey);
    const op = def && freeOp(def);
    if (!op) return;
    const rest = rules.filter((r) => !isConsentRule(r));
    setRules(normalizeRules([...rest, { field: def.key, op: op.key, value: defaultValue(def) }]));
  };
  const changeRule = (i, patch) => {
    const next = rules.map((r, x) => (x === i ? { ...r, ...patch } : r));
    setRules(next);
  };
  const removeRule = (i) => setRules(normalizeRules(rules.filter((r, x) => x !== i || isConsentRule(r))));

  const sample = members.slice(0, SAMPLE_SIZE);
  const bar = [
    { tone: "opted_in", n: counts.opted_in, label: "opted in" },
    { tone: "relationship", n: counts.relationship, label: "relationship" },
    { tone: "no_bulk", n: counts.no_bulk, label: "no bulk, shown but never sent" },
    { tone: "unsubscribed", n: counts.unsubscribed, label: "unsubscribed" },
  ];

  return (
    <div style={SS.detail} data-testid="segment-detail">
      <div style={S.dhead}>
        <div style={S.dback}>
          {/* On a phone the detail takes the screen, so it carries the only way
              back to the list. On a desktop the list is beside it. */}
          {onClose ? (
            <button type="button" style={S.dbackBtn} data-testid="segment-back" disabled={saving} onClick={onClose}>
              ← Segments
            </button>
          ) : <span>Segment</span>}
          <span data-testid="segment-id">
            {isNew ? "not saved yet" : segment.id}
            {segment.source === "chat" ? " · from chat" : ""}
          </span>
        </div>
        {renaming ? (
          <input
            ref={nameRef}
            style={{ ...inp, fontSize: 16, padding: "6px 8px" }}
            value={segment.name || ""}
            aria-label="Segment name"
            data-testid="segment-name-input"
            placeholder="Name this audience"
            onChange={(e) => edit({ name: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setRenaming(false); } }}
          />
        ) : (
          <div style={S.dname} data-testid="segment-name">{segment.name || "Untitled segment"}</div>
        )}
        <div style={S.dco} data-testid="segment-counts">
          Live: {counts.total.toLocaleString()} contact{counts.total === 1 ? "" : "s"} ·{" "}
          {eligible.toLocaleString()} bulk-eligible · recomputed just now
        </div>
        <div style={S.dchips}>
          <button
            type="button"
            style={{ ...btnP, padding: "4px 9px", fontSize: 11, opacity: campaignsTab === null ? 0.5 : 1 }}
            data-testid="segment-use-in-campaign"
            /* A campaign resolves the SAVED record, so offering this while the
               editor holds unsaved rules would hand the campaign an audience
               nobody is looking at. */
            disabled={campaignsTab === null || isNew || dirty || saving}
            title={campaignsTab === null
              ? "The Campaigns tab is not on this build."
              : isNew ? "Save the segment first."
                : dirty ? "Save your changes first — a campaign uses the saved rules, not the ones on screen."
                  : "Start a campaign from this audience"}
            onClick={() => onOpenCampaign?.(segment.id)}
          >
            Use in a campaign
          </button>
          <button type="button" style={{ ...btnS, padding: "4px 9px", fontSize: 11 }}
            data-testid="segment-duplicate" disabled={saving} onClick={onDuplicate}>
            Duplicate
          </button>
          <button type="button" style={{ ...btnS, padding: "4px 9px", fontSize: 11 }}
            data-testid="segment-rename" disabled={saving} onClick={() => setRenaming((v) => !v)}>
            {renaming ? "Done" : "Rename"}
          </button>
          {dirty && (
            <>
              <button type="button" style={{ ...btnP, padding: "4px 9px", fontSize: 11 }}
                data-testid="segment-save" disabled={saving} onClick={onSave}>
                {saving ? "Saving…" : "Save"}
              </button>
              <button type="button" style={{ ...btnS, padding: "4px 9px", fontSize: 11 }}
                data-testid="segment-discard" disabled={saving} onClick={onDiscard}>
                Discard
              </button>
            </>
          )}
          {segment.source === "chat" && (
            <span style={{ ...SS.origin, marginLeft: "auto" }}>/crm · {dateOf(segment.createdAt)}</span>
          )}
        </div>
        {problem && <div style={SS.problem} data-testid="segment-problem">{problem}</div>}
        {/* The resolver's own refusals. A rule nobody can read resolves to
            nobody, and "0 contacts" with no explanation is the worst way to
            learn that the box you are halfway through typing is empty. */}
        {problems.length > 0 && (
          <div style={SS.problem} data-testid="segment-rule-problems">
            {problems.length === 1 ? problems[0] : `${problems[0]} (+${problems.length - 1} more)`}
          </div>
        )}
      </div>

      {/* ── Rules ── */}
      <div style={S.dsec}>
        <div style={S.dsecTitle}>
          <span>Rules</span>
          <span style={{ ...S.dsecAction, cursor: "default" }}>Every rule is live. Match all.</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid="segment-rules">
          {rules.map((rule, i) => (
            <RuleRow
              key={`${rule.field}-${i}`}
              rule={rule}
              index={i}
              stacked={isMobile}
              clients={clients}
              contacts={contacts}
              onChange={(patch) => changeRule(i, patch)}
              onRemove={() => removeRule(i)}
            />
          ))}
          <select
            style={SS.addRule}
            value=""
            aria-label="Add a rule"
            data-testid="segment-add-rule"
            onChange={(e) => { if (e.target.value) addRule(e.target.value); }}
          >
            <option value="">+ Add a rule: role, tag, last touch, value, client, referrer, LinkedIn…</option>
            {addable.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </div>
      </div>

      {/* ── Who is in it ── */}
      <div style={S.dsec}>
        <div style={S.dsecTitle}><span>Who is in it</span></div>
        <div style={SS.stack} data-testid="segment-stack">
          {bar.map((b) => (
            b.n > 0 && (
              <i
                key={b.tone}
                title={`${b.n} ${b.label}`}
                style={{
                  display: "block", height: "100%",
                  width: `${(b.n / Math.max(1, counts.total)) * 100}%`,
                  background: consentColor(b.tone),
                }}
              />
            )
          ))}
        </div>
        <div style={SS.legend} data-testid="segment-legend">
          {bar.map((b) => (
            <span key={b.tone} style={S.consent}>
              <i style={consentDotStyle(b.tone)} />
              {b.n} {b.label}
            </span>
          ))}
        </div>
        <div style={{ ...S.tiles, marginTop: 10 }} data-testid="segment-tiles">
          <div style={S.tile}>
            <div style={S.tileN}>{counts.total.toLocaleString()}</div>
            <div style={S.tileL}>contacts</div>
          </div>
          <div style={S.tile}>
            <div style={S.tileN}>{counts.clients.toLocaleString()}</div>
            <div style={S.tileL}>clients</div>
          </div>
          <div style={S.tile}>
            <div style={S.tileN}>{fmt(counts.quotedValue)}</div>
            <div style={S.tileL}>quoted, never booked</div>
          </div>
        </div>
      </div>

      {/* ── Sample ── */}
      <div style={S.dsec}>
        <div style={S.dsecTitle}>
          <span>Sample</span>
          {counts.total > 0 && (
            <button type="button" style={S.dsecAction} data-testid="segment-see-all"
              onClick={() => setShowAll((v) => !v)}>
              {showAll ? "Show 5" : `See all ${counts.total.toLocaleString()}`}
            </button>
          )}
        </div>
        {counts.total === 0 ? (
          <div style={{ fontSize: 12.5, color: C.muted }} data-testid="segment-sample-empty">
            Nobody matches these rules today. The segment is still live — it will fill as the data changes.
          </div>
        ) : (
          <ul style={SS.members} data-testid="segment-sample">
            {sample.map((ct) => {
              const cell = consentCellOf(ct);
              return (
                <li key={ct.id} style={SS.member}>
                  <span style={{ minWidth: 0 }}>
                    {ct.name || "Unnamed"}
                    {ct.company && <span style={SS.memberCo}> · {ct.company}</span>}
                  </span>
                  <span style={S.consent}>
                    <i style={consentDotStyle(cell.tone)} />
                    {cell.label}
                  </span>
                  <span style={SS.memberWhy}>{memberReason(ct)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ── Used by ── */}
      <div style={S.dsecLast}>
        <div style={S.dsecTitle}><span>Used by</span></div>
        {usedBy.length === 0 ? (
          <div style={{ fontSize: 12.5, color: C.muted }} data-testid="segment-usedby-empty">
            No campaign has used this audience yet.
          </div>
        ) : (
          <ul style={{ listStyle: "none", fontSize: 12.5 }} data-testid="segment-usedby">
            {usedBy.map((c) => (
              <li key={c.id} style={{ padding: "5px 0", borderBottom: `1px solid ${C.border}` }}>
                {c.name || c.title || c.id}
                {c.status ? ` · ${c.status}` : ""}
                {c.frozenAt ? "" : " · audience not frozen yet"}
              </li>
            ))}
          </ul>
        )}
        {!isNew && (
          <button type="button" style={{ ...S.danger, marginTop: 12 }} data-testid="segment-delete"
            disabled={saving} onClick={onDelete}>
            Delete this segment
          </button>
        )}
      </div>

      {showAll && (
        <MembersTable
          members={members}
          excludedBy={excludedBy}
          clients={clients}
          jobs={jobs}
          savedQuotes={savedQuotes}
          name={segment.name}
          onClose={() => setShowAll(false)}
          isMobile={isMobile}
        />
      )}
    </div>
  );
}

/* ═══ one rule row ═════════════════════════════════════════════════════════ */

function defaultValue(def) {
  if (def.value === "days") return 30;
  if (def.value === "months") return 12;
  if (def.value === "money") return { bucket: "any", amount: 0 };
  if (def.value === "contact") return "";
  if (def.value === "none") return null;
  return [];
}

function RuleRow({ rule, index, stacked, clients, contacts, onChange, onRemove }) {
  const def = fieldDef(rule.field);
  const locked = def?.locked;
  const shell = stacked ? { ...SS.rule, ...SS.ruleStacked } : SS.rule;

  if (!def) {
    return (
      <div style={shell} data-testid={`segment-rule-${index}`}>
        <span style={SS.ruleField}>{rule.field}</span>
        <span style={SS.ruleOp}>unknown</span>
        <span style={{ color: C.danger, fontSize: 12 }}>This rule is not one a segment can ask about.</span>
        <button type="button" style={SS.ruleX} aria-label="Remove rule" onClick={onRemove}>×</button>
      </div>
    );
  }

  /* The consent row: drawn dashed, and there is no × on it. It can NARROW —
     "opted in only" is a real audience, the mockup's Website opt-ins — but it
     can never be removed and can never reach past the two bulk-eligible
     states, so the last box on cannot be turned off and no third box exists.
     An operator who could remove the row would be building an audience the
     send would silently refuse anyway. */
  if (locked) {
    const on = consentValue(rule.value);
    const toggle = (state) => {
      const next = on.includes(state) ? on.filter((s) => s !== state) : [...on, state];
      // The guard is belt and braces: the last checked box is also disabled.
      if (!next.length) return;
      onChange({ value: consentValue(next) });
    };
    return (
      <div style={{ ...shell, ...SS.ruleLocked }} data-testid="segment-rule-consent">
        <span style={SS.ruleField}>{def.label}</span>
        <span style={SS.ruleOp}>is</span>
        <span style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {BULK_ELIGIBLE.map((state) => {
            const checked = on.includes(state);
            const last = checked && on.length === 1;
            return (
              <label
                key={state}
                style={{ ...S.consent, cursor: last ? "default" : "pointer" }}
                title={last ? "An audience needs at least one consent state." : ""}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={last}
                  data-testid={`segment-consent-${state}`}
                  style={{ accentColor: C.text, margin: 0 }}
                  onChange={() => toggle(state)}
                />
                <i style={consentDotStyle(state)} />
                {CONSENT_STATES[state] || state}
              </label>
            );
          })}
        </span>
        <span
          style={SS.ruleLockedTag}
          title="This row is always on a segment and cannot be removed."
          data-testid="segment-rule-consent-locked"
        >
          locked
        </span>
      </div>
    );
  }

  return (
    <div style={shell} data-testid={`segment-rule-${rule.field}`}>
      <span style={SS.ruleField}>{def.label}</span>
      {def.ops.length > 1 ? (
        <select
          style={SS.ruleSelect}
          aria-label={`${def.label} comparison`}
          data-testid={`segment-rule-op-${rule.field}`}
          value={rule.op}
          onChange={(e) => onChange({ op: e.target.value })}
        >
          {def.ops.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      ) : (
        <span style={SS.ruleOp}>{def.ops[0].label}</span>
      )}
      <RuleValue rule={rule} def={def} clients={clients} contacts={contacts} onChange={onChange} />
      <button
        type="button"
        style={SS.ruleX}
        aria-label={`Remove the ${def.label} rule`}
        data-testid={`segment-rule-remove-${rule.field}`}
        onClick={onRemove}
      >
        ×
      </button>
    </div>
  );
}

function RuleValue({ rule, def, clients, contacts, onChange }) {
  const testid = `segment-rule-value-${rule.field}`;

  if (def.value === "none") return <span style={SS.ruleOp}>—</span>;

  if ((rule.field === "quote" || rule.field === "job") && rule.op === "none") {
    return <span style={SS.ruleOp} data-testid={testid}>ever</span>;
  }

  // "Last touch never" asks about the absence of a date; there is no number.
  if (rule.field === "last_touch" && rule.op === "never") {
    return <span style={SS.ruleOp} data-testid={testid}>at all</span>;
  }

  if (def.value === "days" || def.value === "months") {
    return (
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input
          type="number"
          min="1"
          step="1"
          style={SS.ruleNumber}
          aria-label={`${def.label} ${def.value}`}
          data-testid={testid}
          value={rule.value ?? ""}
          onChange={(e) => onChange({ value: e.target.value === "" ? "" : Number(e.target.value) })}
        />
        <span style={SS.ruleOp}>{def.value}</span>
      </span>
    );
  }

  if (def.value === "money") {
    return (
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <select
          style={SS.ruleSelect}
          aria-label="Value bucket"
          data-testid={`segment-rule-bucket-${rule.field}`}
          value={rule.value?.bucket || "any"}
          onChange={(e) => onChange({ value: { ...(rule.value || {}), bucket: e.target.value } })}
        >
          {VALUE_BUCKETS.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
        </select>
        <input
          type="number"
          min="0"
          step="100"
          style={SS.ruleNumber}
          aria-label="Dollars"
          data-testid={testid}
          value={rule.value?.amount ?? ""}
          onChange={(e) => onChange({
            value: { bucket: rule.value?.bucket || "any", amount: e.target.value === "" ? "" : Number(e.target.value) },
          })}
        />
      </span>
    );
  }

  if (def.value === "contact") {
    return (
      <select
        style={SS.ruleSelect}
        aria-label="Referrer"
        data-testid={testid}
        value={rule.value || ""}
        onChange={(e) => onChange({ value: e.target.value })}
      >
        <option value="">Pick a contact…</option>
        {[...contacts].sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
          .map((c) => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
      </select>
    );
  }

  if (def.value === "tags") {
    return (
      <input
        style={SS.ruleText}
        aria-label="Tags"
        placeholder="comma separated"
        data-testid={testid}
        value={arr(rule.value).join(", ")}
        onChange={(e) => onChange({ value: parseTags(e.target.value) })}
      />
    );
  }

  // keys and clients: chosen values as removable chips, plus a picker.
  const options = def.value === "clients"
    ? [...clients].sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
      .map((c) => [c.id, c.name || c.id])
    : Object.entries(def.options || {});
  const chosen = arr(rule.value);
  const label = (v) => options.find(([k]) => k === v)?.[1] || v;

  return (
    <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", minWidth: 0 }}>
      {chosen.map((v) => (
        <span key={v} style={S.tag}>
          {label(v)}
          <button
            type="button"
            style={SS.chipX}
            aria-label={`Remove ${label(v)}`}
            onClick={() => onChange({ value: chosen.filter((x) => x !== v) })}
          >
            ×
          </button>
        </span>
      ))}
      <select
        style={SS.ruleSelect}
        aria-label={def.label}
        data-testid={testid}
        value=""
        onChange={(e) => { if (e.target.value) onChange({ value: [...chosen, e.target.value] }); }}
      >
        <option value="">{chosen.length ? "Add…" : "Pick…"}</option>
        {options.filter(([k]) => !chosen.includes(k)).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
    </span>
  );
}

/* ═══ the full list ════════════════════════════════════════════════════════ */

/** The Contacts columns, over the members of one segment. Same derivation as
 *  the Contacts tab (buildContactRows), so a person reads the same on both. */
function MembersTable({ members, excludedBy, clients, jobs, savedQuotes, name, onClose, isMobile }) {
  const rows = useMemo(
    () => buildContactRows({ contacts: members, clients, jobs, quotes: savedQuotes }),
    [members, clients, jobs, savedQuotes],
  );
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); onClose(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div style={SS.overlay} role="dialog" aria-label={`Everyone in ${name || "this segment"}`} data-testid="segment-members">
      <div style={{ ...SS.overlayPanel, padding: isMobile ? 12 : 20 }}>
        <div style={SS.overlayHead}>
          <div>
            <div style={S.dname}>{name || "Untitled segment"}</div>
            <div style={S.dco}>{rows.length.toLocaleString()} contact{rows.length === 1 ? "" : "s"}, everyone the rules match</div>
          </div>
          <button type="button" style={{ ...btnS, padding: "5px 12px" }} data-testid="segment-members-close" onClick={onClose}>
            Close
          </button>
        </div>
        <div style={{ ...S.box, overflowY: "auto", maxHeight: "70vh" }}>
          <ListRowStyle />
          <div style={S.scroller}>
            <table style={S.table} data-testid="segment-members-table">
              <thead>
                <tr>
                  <th style={S.th}>Contact</th>
                  <th style={S.th}>Role</th>
                  <th style={S.th}>Consent</th>
                  <th style={{ ...S.th, ...S.thRight }}>Value</th>
                  <th style={S.th}>Last touch</th>
                  <th style={S.th}>LinkedIn</th>
                  <th style={S.th}>Tags</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={ROW_CLASS} data-testid={`segment-member-${r.id}`} style={{ height: 44 }}>
                    <td style={cellStyle(null, false, true)}>
                      <div style={S.who}>
                        <div style={S.whoName}>{r.name || "Unnamed"}</div>
                        <div style={S.whoMeta}>
                          {r.company}
                          {r.company && r.email && <span style={S.dot}>·</span>}
                          {r.email}
                        </div>
                      </div>
                    </td>
                    <td style={cellStyle()}>
                      {r.roles.map((role) => (
                        <span key={role} style={{
                          ...S.role,
                          background: `${ROLE_COLORS[role] || C.muted}12`,
                          color: ROLE_COLORS[role] || C.muted,
                        }}>
                          {CONTACT_ROLES[role] || role}
                        </span>
                      ))}
                    </td>
                    <td style={cellStyle()}>
                      <span style={S.consent}>
                        <i style={consentDotStyle(r.consent.tone)} />
                        {r.consent.label}
                        {excludedBy[r.id] && <span style={{ ...S.small, marginLeft: 4 }}>held back</span>}
                      </span>
                    </td>
                    <td style={cellStyle({ ...S.mono, ...S.tdRight })}>
                      {r.value.amount === null ? "—" : fmt(r.value.amount)}
                      {r.value.note && (
                        <span style={S.small}>
                          {r.value.quoted ? `quoted ${fmt(r.value.quoted)}` : r.value.note}
                        </span>
                      )}
                    </td>
                    <td style={cellStyle(S.touch)}>
                      {r.touchRel
                        ? <><span style={S.touchNow}>{r.touchRel}</span>{r.touchRel === "today" ? "" : " ago"}</>
                        : "—"}
                      {r.touch.label && <span style={S.touchChannel}>{r.touch.label}</span>}
                    </td>
                    <td style={cellStyle()}>
                      {r.linkedin.connected
                        ? <span style={S.linkedin}>connected</span>
                        : <span style={S.linkedinOff}>{r.linkedin.url ? "not connected" : "—"}</span>}
                    </td>
                    <td style={cellStyle()}>
                      {r.tags.map((t) => <span key={t} style={S.tag}>{t}</span>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

/* The Consent cell for one contact, without going through buildContactRows —
   the sample list needs the label and the dot, not the whole row. */
function consentCellOf(contact) {
  const c = contact?.consent || {};
  const unsubscribed = c.unsubscribed === true;
  const state = CONSENT_STATES[c.state] ? c.state : "no_bulk";
  return {
    tone: unsubscribed ? "unsubscribed" : state,
    label: unsubscribed ? "Unsubscribed" : CONSENT_STATES[state],
  };
}

/* ═══ styles ═══════════════════════════════════════════════════════════════
   The mockup's #segments block, in tokens. Nothing new: where it draws a
   lighter grey than --muted this uses C.muted at reduced opacity, the same
   trade the two list views make. */
const SS = {
  list: {
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
    overflow: "hidden", fontFamily: font,
  },
  listHead: {
    padding: "10px 12px", borderBottom: `1px solid ${C.border}`,
    display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap",
  },
  /* Tighter than the shared chip: four of them, a count and a button share
     400px, and the mockup draws that on one line. */
  listChip: { padding: "4px 8px", fontSize: 10.5 },
  row: {
    padding: "11px 12px", borderBottom: `1px solid ${C.border}`, cursor: "pointer",
    display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 10, alignItems: "start",
  },
  rowSel: { background: C.bg, boxShadow: `inset 3px 0 0 ${C.text}` },
  rowName: { fontSize: 13, fontWeight: 400 },
  rowDef: { fontSize: 11.5, color: C.muted, marginTop: 2, lineHeight: 1.4 },
  rowCount: { fontFamily: mono, fontSize: 14, fontVariantNumeric: "tabular-nums" },
  rowEligible: { fontSize: 10.5, color: C.muted },
  rowMeta: { gridColumn: "1 / 3", fontFamily: mono, fontSize: 10, color: C.muted, opacity: 0.8, marginTop: 2 },

  detail: {
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
    overflow: "hidden", fontFamily: font,
  },
  origin: { fontFamily: mono, fontSize: 10.5, color: C.purple },
  problem: {
    marginTop: 8, fontSize: 12, color: C.danger, background: `${C.danger}0F`,
    border: `1px solid ${C.danger}33`, borderRadius: 6, padding: "6px 9px",
  },

  rule: {
    display: "grid", gridTemplateColumns: "110px 100px minmax(0,1fr) auto", gap: 8,
    alignItems: "center", background: C.bg, border: `1px solid ${C.border}`,
    borderRadius: 6, padding: "6px 10px", fontSize: 12.5,
  },
  ruleStacked: { display: "flex", flexWrap: "wrap", gap: "6px 8px" },
  ruleLocked: { borderStyle: "dashed" },
  ruleLockedTag: { fontFamily: mono, fontSize: 10, color: C.capture },
  ruleField: { color: C.text },
  ruleOp: { fontFamily: mono, fontSize: 10.5, color: C.muted, opacity: 0.85 },
  ruleX: {
    fontFamily: mono, fontSize: 12, color: C.muted, opacity: 0.85, background: "transparent",
    border: "none", cursor: "pointer", padding: "0 2px",
  },
  chipX: {
    fontFamily: mono, fontSize: 10, color: C.muted, background: "transparent",
    border: "none", cursor: "pointer", padding: "0 0 0 4px",
  },
  ruleSelect: {
    ...sel, width: "auto", maxWidth: "100%", padding: "3px 22px 3px 6px", fontSize: 11.5,
    background: C.surface, backgroundImage: sel.backgroundImage, backgroundRepeat: "no-repeat",
    backgroundPosition: "right 6px center", borderRadius: 4,
  },
  ruleNumber: { ...inp, width: 74, padding: "3px 6px", fontSize: 11.5, background: C.surface },
  ruleText: { ...inp, padding: "3px 6px", fontSize: 11.5, background: C.surface },
  addRule: {
    ...sel, fontSize: 11.5, color: C.muted, background: "transparent", backgroundImage: "none",
    border: `1px dashed ${C.border}`, borderRadius: 6, padding: "6px 10px", textAlign: "left",
  },

  stack: {
    display: "flex", height: 8, borderRadius: 2, overflow: "hidden",
    background: C.surfaceAlt, margin: "6px 0",
  },
  legend: { display: "flex", gap: 14, fontSize: 11, color: C.muted, flexWrap: "wrap" },

  members: { listStyle: "none", fontSize: 12.5 },
  member: {
    display: "grid", gridTemplateColumns: "minmax(0,1fr) auto auto", gap: 10,
    padding: "6px 0", borderBottom: `1px solid ${C.border}`, alignItems: "center",
  },
  memberCo: { color: C.muted, fontSize: 11.5 },
  memberWhy: { fontFamily: mono, fontSize: 10.5, color: C.muted, opacity: 0.85, whiteSpace: "nowrap" },

  overlay: {
    position: "fixed", inset: 0, background: "rgba(26,26,26,0.35)", zIndex: 60,
    display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflowY: "auto",
  },
  overlayPanel: {
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
    width: "min(1100px, 100%)", fontFamily: font,
  },
  overlayHead: { display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 },
};
